import React from "react";
import { Box, Flex, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { motion } from "framer-motion";
import {
  PiTextAaDuotone, PiCalendarBlankDuotone, PiListChecksDuotone, PiUsersFourDuotone, PiFileTextDuotone,
  PiCheckBold, PiSparkleDuotone, PiWarningCircleDuotone
} from "react-icons/pi";
import { CreateVotingViewModel } from "../CreateVotingView/CreateVotingViewModel";
import { drgba, EASE } from "../../components/glassUi";

type Meta = { vkey: string; label: string; icon: React.ElementType; color: string; target: string };

export const RequirementsChecklist: React.FC<{ model: CreateVotingViewModel; onGoto?: (target: string) => void }> = observer(({ model, onGoto }) => {
  const issues = model.validationIssues;
  const msgsFor = (t: string) => issues.filter(i => i.target === t).map(i => i.message);
  const closed = model.electorateType === "closed";

  const base: Meta[] = [
    { vkey: "name", label: "Tytuł głosowania", icon: PiTextAaDuotone, color: "#2563eb", target: "cv-title" },
    { vkey: "term", label: "Termin głosowania", icon: PiCalendarBlankDuotone, color: "#7b2ff7", target: "cv-term" },
    { vkey: "questions", label: "Pytania", icon: PiListChecksDuotone, color: "#0d9488", target: "cv-questions" }
  ];
  const items = [
    ...base,
    ...(closed ? [{ vkey: "voters", label: "Uprawnieni", icon: PiUsersFourDuotone, color: "#e51f44", target: "cv-voters" } as Meta] : []),
    ...(msgsFor("docs").length ? [{ vkey: "docs", label: "Kworum i dokumenty", icon: PiFileTextDuotone, color: "#f59e0b", target: "cv-extras" } as Meta] : [])
  ].map(it => { const msgs = msgsFor(it.vkey); return { ...it, msgs, done: msgs.length === 0 }; });

  const doneCount = items.filter(i => i.done).length;
  const total = items.length;
  const allDone = doneCount === total;
  const pct = total ? Math.round((doneCount / total) * 100) : 0;

  return (
    <Box borderRadius="18px" p="16px" bg="rgba(255,255,255,0.55)" border="1px solid rgba(24,12,48,0.06)" boxShadow="inset 0 1px 0 rgba(255,255,255,0.7)">
      <Flex align="center" justify="space-between" mb="12px">
        <Flex align="center" gap="8px">
          <Icon as={allDone ? PiSparkleDuotone : PiListChecksDuotone} boxSize="17px" color={allDone ? "#10b981" : "#7b2ff7"} />
          <Text fontSize="12px" fontWeight="900" letterSpacing="0.04em" textTransform="uppercase" color={allDone ? "#10b981" : "#6b6480"}>
            {allDone ? "Gotowe do publikacji" : "Do uzupełnienia"}
          </Text>
        </Flex>
        <Text fontSize="12px" fontWeight="800" color={allDone ? "#10b981" : "#a29bb2"}>{doneCount}/{total}</Text>
      </Flex>

      <Box h="6px" borderRadius="full" bg="rgba(24,12,48,0.08)" overflow="hidden" mb="14px">
        <motion.div initial={false} animate={{ width: `${pct}%` }} transition={{ type: "spring", stiffness: 260, damping: 30 }} style={{ height: "100%", borderRadius: 999, background: allDone ? "#10b981" : "linear-gradient(90deg,#7b2ff7,#2563eb)" }} />
      </Box>

      <VStack align="stretch" spacing="8px">
        {items.map(it => (
          <motion.div
            key={it.vkey + (it.done ? "-done" : "-todo")}
            initial={it.done ? { scale: 0.68, opacity: 0 } : false}
            animate={it.done ? { scale: 1, opacity: 1, boxShadow: ["0 0 0 0px rgba(16,185,129,0.5)", "0 0 0 12px rgba(16,185,129,0)"] } : { scale: 1, opacity: 1 }}
            transition={{ default: { type: "spring", stiffness: 420, damping: 15 }, boxShadow: { duration: 0.7, ease: "easeOut" } }}
            style={{ borderRadius: 13 }}
          >
            <Flex as="button" type="button" w="100%" textAlign="left" onClick={() => onGoto?.(it.target)}
              align="flex-start" gap="11px" p="10px 11px" borderRadius="13px" transition={`all ${EASE}`}
              bg={it.done ? drgba("#10b981", 0.12) : "rgba(24,12,48,0.04)"}
              border="1px solid" borderColor={it.done ? drgba("#10b981", 0.32) : "transparent"}
              _hover={{ borderColor: it.done ? drgba("#10b981", 0.5) : "rgba(24,12,48,0.14)", transform: "translateX(2px)" }}
              _active={{ transform: "translateX(0)" }}>
              <Flex w="30px" h="30px" borderRadius="9px" align="center" justify="center" flexShrink={0} mt="1px"
                bg={it.done ? "#10b981" : "rgba(24,12,48,0.09)"} transition={`background ${EASE}`}>
                {it.done ? (
                  <motion.span initial={{ scale: 0, rotate: -100 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 560, damping: 18 }} style={{ display: "flex" }}>
                    <Icon as={PiCheckBold} boxSize="15px" color="white" />
                  </motion.span>
                ) : (
                  <Icon as={it.icon} boxSize="16px" color="#a29bb2" />
                )}
              </Flex>
              <Box flex="1" minW={0}>
                <Text fontSize="13.5px" fontWeight={it.done ? "800" : "700"} color={it.done ? "#0f7a52" : "#241536"}>{it.label}</Text>
                {!it.done && (
                  <VStack align="stretch" spacing="4px" mt="5px">
                    {it.msgs.map((m, mi) => (
                      <HStack key={mi} align="flex-start" spacing="6px">
                        <Icon as={PiWarningCircleDuotone} boxSize="13px" color="#e08a00" mt="1px" flexShrink={0} />
                        <Text fontSize="12px" color="#8a6d3b" lineHeight="1.4">{m}</Text>
                      </HStack>
                    ))}
                  </VStack>
                )}
              </Box>
            </Flex>
          </motion.div>
        ))}
      </VStack>
    </Box>
  );
});
